import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ExpenseProvider } from "@/contexts/ExpenseContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { UserProvider } from "@/contexts/UserContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { AccessibilityProvider } from "@/contexts/AccessibilityContext";
import { Layout } from "@/components/layout/Layout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AuthGuard } from "@/components/AuthGuard";
import { SkipToMain } from "@/components/SkipLink";
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import Transactions from "./pages/Transactions";
import Categories from "./pages/Categories";
import PeriodicPayments from "./pages/PeriodicPayments";
import AddExpense from "./pages/AddExpense";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import WhatsAppConfig from "./pages/WhatsAppConfig";
import GoogleSheetsConfig from "./pages/GoogleSheetsConfig";
import Users from "./pages/Users";
import Login from "./pages/Login";
import Contact from "./pages/Contact";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Accessibility from "./pages/Accessibility";
import AccessibilityStatement from "./pages/AccessibilityStatement";
import NotFound from "./pages/NotFound";
import Admin from "./pages/Admin";
import TransactionReview from "./pages/TransactionReview";
import IPhoneAutomationGuide from "./pages/IPhoneAutomationGuide";
import CreditCardSync from "./pages/CreditCardSync";
import AiAgents from "./pages/AiAgents";
// TODO: Finance Agent (AI chat) feature is incomplete (no API client / backend yet). Disabled for now.
// import AiChat from "./pages/AiChat";
import { useFeatureFlags } from "./hooks/useFeatureFlags";
import { AI_AGENTS_TAB_ENABLED } from "./lib/featureFlags";
import "./i18n";

const queryClient = new QueryClient();

function FeatureGatedCreditCardSync() {
  const { creditCardSync, loading } = useFeatureFlags();
  if (loading) return null;
  if (!creditCardSync) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <p className="text-muted-foreground">This feature is not available for your account.</p>
      </div>
    );
  }
  return <CreditCardSync />;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <LanguageProvider>
        <AccessibilityProvider>
          <CurrencyProvider>
            <UserProvider>
              <ExpenseProvider>
                <Toaster />
                <Sonner />
                <BrowserRouter>
                  <SkipToMain />
                  <AuthGuard>
                  <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/accessibility" element={<Accessibility />} />
                    <Route path="/accessibility-statement" element={<AccessibilityStatement />} />
                    {/* Transaction Review - Public route with token-based access */}
                    <Route path="/review/:token" element={<TransactionReview />} />
                    <Route path="/dashboard" element={
                      <ProtectedRoute>
                        <Layout>
                          <Dashboard />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/transactions" element={
                      <ProtectedRoute>
                        <Layout>
                          <Transactions />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/categories" element={
                      <ProtectedRoute>
                        <Layout>
                          <Categories />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    {AI_AGENTS_TAB_ENABLED && (
                      <Route path="/ai-agents" element={
                        <ProtectedRoute>
                          <Layout>
                            <AiAgents />
                          </Layout>
                        </ProtectedRoute>
                      } />
                    )}
                    {/* TODO: Finance Agent (AI chat) routes disabled until the feature is complete.
                    {AI_CHAT_TAB_ENABLED && (
                      <>
                        <Route path="/finance-agent" element={
                          <ProtectedRoute>
                            <Layout>
                              <AiChat />
                            </Layout>
                          </ProtectedRoute>
                        } />
                        <Route path="/ai-chat" element={<Navigate to="/finance-agent" replace />} />
                      </>
                    )}
                    */}
                    <Route path="/periodic-payments" element={
                      <ProtectedRoute>
                        <Layout>
                          <PeriodicPayments />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/add-expense" element={
                      <ProtectedRoute>
                        <Layout>
                          <AddExpense />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/profile" element={
                      <ProtectedRoute>
                        <Layout>
                          <Profile />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/settings" element={
                      <ProtectedRoute>
                        <Layout>
                          <Settings />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/whatsapp-config" element={
                      <ProtectedRoute>
                        <Layout>
                          <WhatsAppConfig />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/iphone-automation-guide" element={
                      <ProtectedRoute>
                        <Layout>
                          <IPhoneAutomationGuide />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/integrations/credit-cards" element={
                      <ProtectedRoute>
                        <Layout>
                          <FeatureGatedCreditCardSync />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/integrations/google-sheets" element={
                      <ProtectedRoute>
                        <Layout>
                          <GoogleSheetsConfig />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/users" element={
                      <ProtectedRoute>
                        <Layout>
                          <Users />
                        </Layout>
                      </ProtectedRoute>
                    } />
                    {/* Hidden Admin Route - accessible only by typing the URL directly */}
                    <Route path="/admin-panel" element={<Admin />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </AuthGuard>
              </BrowserRouter>
            </ExpenseProvider>
          </UserProvider>
        </CurrencyProvider>
      </AccessibilityProvider>
    </LanguageProvider>
  </TooltipProvider>
</QueryClientProvider>
);

export default App; 